import React, { useCallback, useState } from "react";
import { Box, Grid } from "@mui/material";
import { connect } from 'react-redux'
import { setCart, setFavorites, setSnackBar } from '../models/application/applicationActions'
import { State } from '../models/application/applicationReducer'
import LoginForm from "../components/Forms/LoginForm"
import { makeStyles } from "@mui/styles";
import { useHistory } from "react-router";
import ForgotPasswordFormDialog from "../components/Dialogs/ForgotPasswordFormDialog";
import RegisterForm from "../components/Forms/RegisterForm";
import { initCartAndFavs } from "../helpers/utilities";
import { login } from "../models/user/authActions";
import { Title } from "../components/Typography/Title";
import { environment } from "../environment/environment";
import { FacebookButton } from "../components/Button/FacebookButton";
import { GoogleButton } from "../components/Button/GoogleButton";
import { loginFacebook, loginGoogle } from "../models/user/service";
import { User } from "../models/user/user";
import { SMSDialog } from "../components/Dialogs/SMSDialog";

const FACEBOOK_SDK_ID = "facebook-jssdk";
const FACEBOOK_SDK_VERSION = "v18.0";
const FACEBOOK_LOGIN_SCOPE = "public_profile,email";
let facebookSdkPromise: Promise<any> | undefined;
const GOOGLE_SDK_ID = "google-identity-services";
let googleSdkPromise: Promise<any> | undefined;

const getFacebookRedirectUri = () => `${window.location.origin}/login`

const getFacebookAccessTokenFromHash = () => {
  const hash = window.location.hash.startsWith("#")
    ? window.location.hash.substring(1)
    : window.location.hash
  const params = new URLSearchParams(hash)
  const token = params.get("access_token")
  if (!token) return null
  const expectedState = sessionStorage.getItem("facebookLoginState")
  sessionStorage.removeItem("facebookLoginState")
  if (!expectedState || params.get("state") !== expectedState) {
    throw new Error("Login session expired. Please try again.")
  }
  return token
}

const clearFacebookHash = () => {
  if (!window.location.hash) {
    return
  }

  window.history.replaceState(
    null,
    document.title,
    `${window.location.pathname}${window.location.search}`
  )
}

const redirectToFacebookLogin = () => {
  const random = window.crypto.getRandomValues(new Uint8Array(32))
  const state = Array.from(random, byte => byte.toString(16).padStart(2, "0")).join("")
  sessionStorage.setItem("facebookLoginState", state)
  const params = new URLSearchParams({
    client_id: environment.facebookAPPID,
    redirect_uri: getFacebookRedirectUri(),
    response_type: "token",
    scope: FACEBOOK_LOGIN_SCOPE,
    auth_type: "rerequest",
    state
  })

  window.location.href = `https://www.facebook.com/${FACEBOOK_SDK_VERSION}/dialog/oauth?${params.toString()}`
}

const initializeFacebookSdk = (FB: any) => {
  FB.init({
    appId: environment.facebookAPPID,
    cookie: true,
    xfbml: false,
    version: FACEBOOK_SDK_VERSION
  })
  return FB
}

const waitForFacebookSdk = (resolve: (FB: any) => void, reject: (error: Error) => void, timeout: number) => {
  const startedAt = Date.now()
  const facebookWindow = window as any

  const interval = window.setInterval(() => {
    if (facebookWindow.FB) {
      window.clearInterval(interval)
      resolve(initializeFacebookSdk(facebookWindow.FB))
      return
    }

    if (Date.now() - startedAt >= timeout) {
      window.clearInterval(interval)
      facebookSdkPromise = undefined
      reject(new Error("Facebook SDK load timed out"))
    }
  }, 100)
}

const loadFacebookSdk = () => {
  if (facebookSdkPromise) {
    return facebookSdkPromise
  }

  facebookSdkPromise = new Promise<any>((resolve, reject) => {
    const facebookWindow = window as any

    if (facebookWindow.FB) {
      resolve(initializeFacebookSdk(facebookWindow.FB))
      return
    }

    facebookWindow.fbAsyncInit = () => {
      if (facebookWindow.FB) {
        resolve(initializeFacebookSdk(facebookWindow.FB))
      } else {
        facebookSdkPromise = undefined
        reject(new Error("Facebook SDK did not initialize"))
      }
    }

    const existingScript = document.getElementById(FACEBOOK_SDK_ID)
    if (existingScript) {
      waitForFacebookSdk(resolve, reject, 10000)
      return
    }

    const script = document.createElement("script")
    script.id = FACEBOOK_SDK_ID
    script.async = true
    script.defer = true
    script.crossOrigin = "anonymous"
    script.src = "https://connect.facebook.net/en_US/sdk.js"
    script.onerror = () => {
      facebookSdkPromise = undefined
      reject(new Error("Facebook SDK failed to load"))
    }

    const firstScript = document.getElementsByTagName("script")[0]
    firstScript?.parentNode?.insertBefore(script, firstScript)
    waitForFacebookSdk(resolve, reject, 10000)
  })

  return facebookSdkPromise
}

const loadGoogleSdk = () => {
  if (googleSdkPromise) {
    return googleSdkPromise
  }

  googleSdkPromise = new Promise<any>((resolve, reject) => {
    const googleWindow = window as any

    if (googleWindow.google?.accounts?.id) {
      resolve(googleWindow.google)
      return
    }

    const existingScript = document.getElementById(GOOGLE_SDK_ID)
    if (existingScript) {
      existingScript.addEventListener("load", () => resolve(googleWindow.google))
      existingScript.addEventListener("error", () => reject(new Error("Google SDK failed to load")))
      return
    }

    const script = document.createElement("script")
    script.id = GOOGLE_SDK_ID
    script.async = true
    script.defer = true
    script.src = "https://accounts.google.com/gsi/client"
    script.onload = () => resolve(googleWindow.google)
    script.onerror = () => {
      googleSdkPromise = undefined
      reject(new Error("Google SDK failed to load"))
    }

    const firstScript = document.getElementsByTagName("script")[0]
    firstScript?.parentNode?.insertBefore(script, firstScript)
  })

  return googleSdkPromise
}

const LoginPage = (props) => {
  const { app, login, setCart, auth, setFavorites, translations, setSnackBar } = props
  const facebookLoginEnabled = app.featureFlags?.facebook_login === true
  const googleLoginEnabled = app.featureFlags?.google_login === true
  const socialLoginEnabled = facebookLoginEnabled || googleLoginEnabled
  const [smsDialogOpen, setSmsDialogOpen] = useState(false)
  const [user, setUser] = useState<User>()
  const [smsDialogType, setSMSDialogType] = useState("")
  const [socialValidationMode, setSocialValidationMode] = useState<"email" | "phone">("phone")
  const styles = useStyles()
  const history = useHistory();
  const [snackbar, setSnackbar] = useState<State>({ open: false, vertical: 'bottom', horizontal: 'center', text: '' })
  const [passwordRestoreOpen, setPasswordRestoreOpen] = useState(false)
  const handleSnackbarClose = () => {
    setSnackBar({ vertical: 'bottom', horizontal: 'center', text: '', open: false })
  };
  React.useEffect(() => {
    setSnackbar(app.snackBar)
  }, [app.snackBar])

  const showFacebookLoginError = (detail = "") => {
    const message = detail
      ? `Facebook login failed: ${detail}`
      : translations?.error || "Facebook login failed. Please try again."

    setSnackBar({
      ...app.snackBar,
      open: true,
      text: message,
      bColor: "#d32f2f"
    })
  }

  const showGoogleLoginError = (detail = "") => {
    const message = detail
      ? `Google login failed: ${detail}`
      : translations?.error || "Google login failed. Please try again."

    setSnackBar({
      ...app.snackBar,
      open: true,
      text: message,
      bColor: "#d32f2f"
    })
  }

  const finishSocialLogin = (resp: User | any, provider: "facebook" | "google") => {
    if (resp?.accessToken) {
      if (resp.activated) {
        let user = new User()
        user = user.updateUser(resp)
        login(user)
        initCartAndFavs(app, { ...auth, loggedIn: true }, setCart, setFavorites)
        history.push("/")
        setSnackBar({ ...app.snackBar, open: true, text: `${translations?.welcome} ${user.firstName}.`, bColor: "rgb(83, 148, 96)" })
      } else {
        setUser(resp as User)
        setSMSDialogType(provider)
        setSocialValidationMode(resp.email ? "email" : "phone")
        setSmsDialogOpen(true)
      }
      return
    }

    if (provider === "google") {
      showGoogleLoginError()
    } else {
      showFacebookLoginError()
    }
  }

  const handleFacebookLogin = (response: any) => {
    if (!response?.accessToken) {
      showFacebookLoginError()
      return
    }

    localStorage.setItem("facebookAccessToken", response.accessToken)
    loginFacebook(response.accessToken).subscribe((resp: User | any) => {
      finishSocialLogin(resp, "facebook")
    })
  }

  React.useEffect(() => {
    if (!facebookLoginEnabled) return
    let accessToken: string | null
    try {
      accessToken = getFacebookAccessTokenFromHash()
    } catch (error) {
      clearFacebookHash()
      showFacebookLoginError("Login session expired. Please try again.")
      return
    }
    if (accessToken) {
      clearFacebookHash()
      handleFacebookLogin({ accessToken })
      return
    }

    loadFacebookSdk().catch(() => undefined)
  }, [facebookLoginEnabled])

  const handleGoogleLogin = React.useCallback((credential: string) => {
    localStorage.setItem("googleCredential", credential)
    loginGoogle(credential).subscribe((resp: User | any) => {
      finishSocialLogin(resp, "google")
    })
  }, [app, auth, translations])

  const loginWithGoogle = () => {
    if (!googleLoginEnabled) return
    setSnackBar({
      ...app.snackBar,
      open: true,
      text: "Opening Google login...",
      bColor: "rgb(83, 148, 96)"
    })

    loadGoogleSdk()
      .then((google) => {
        google.accounts.id.initialize({
          client_id: environment.googleClientID,
          callback: (response: any) => {
            if (response?.credential) {
              handleGoogleLogin(response.credential)
            } else {
              console.error("Google login response", response)
              showGoogleLoginError("no credential returned")
            }
          },
        })

        google.accounts.id.prompt((notification: any) => {
          if (notification.isNotDisplayed?.()) {
            showGoogleLoginError(notification.getNotDisplayedReason?.() || "prompt not displayed")
          } else if (notification.isSkippedMoment?.()) {
            showGoogleLoginError(notification.getSkippedReason?.() || "prompt skipped")
          }
        })
      })
      .catch((error) => {
        console.error("Google SDK error", error)
        showGoogleLoginError(error?.message || "SDK failed to load")
      })
  }

  const loginWithFacebook = () => {
    if (!facebookLoginEnabled) return
    setSnackBar({
      ...app.snackBar,
      open: true,
      text: "Opening Facebook login...",
      bColor: "rgb(83, 148, 96)"
    })

    redirectToFacebookLogin()
  }

  const RenderSMSDialog = useCallback(() => {
    return <SMSDialog
      textAccept={translations?.send}
      onSuccess={(type) => {
        if (type === "google") {
          const credential = localStorage.getItem("googleCredential")
          if (!credential) {
            showGoogleLoginError()
            return
          }

          loginGoogle(credential).subscribe((resp: User | any) => {
            finishSocialLogin(resp, "google")
          })
        } else {
          const accessToken = localStorage.getItem("facebookAccessToken")
          if (!accessToken) {
            showFacebookLoginError()
            return
          }

          loginFacebook(accessToken).subscribe((resp: User | any) => {
            finishSocialLogin(resp, "facebook")
          })
        }
      }}
      user={user}
      type={smsDialogType}
      validationMode={socialValidationMode}
      open={smsDialogOpen} {...props}
      setOpen={setSmsDialogOpen}
    />
  }, [smsDialogOpen, smsDialogType, socialValidationMode, user])

  return <div className={styles.container} style={{ alignItems: 'center', justifyContent: 'center' }}>
    <RenderSMSDialog />
    <ForgotPasswordFormDialog
      translations={translations}
      onOpen={passwordRestoreOpen} onClose={setPasswordRestoreOpen} />
    <Grid container spacing={1} style={{ maxWidth: 1200, margin: '0px auto' }}>
      {window.innerWidth < 1200 && socialLoginEnabled ? <><Box sx={{ padding: 0, paddingLeft: 2, width: '100%', marginTop: 5 , marginBottom: 2 }}>
        <Title text={translations?.authentication_facebook}
          style={{ padding: 0 }}
          fontSize={24} uppercase={true} fontWeight='500' />
      </Box>
        <Box style={{ width: '100%', maxWidth: 481, paddingBottom: 0  }}>
          <Grid item lg={12} sm={12} style={{ width: '100%'}}>
            {facebookLoginEnabled && <FacebookButton title={"Facebook"} onClick={loginWithFacebook} />}
          </Grid>
          {googleLoginEnabled && <GoogleButton title={"Google"} onClick={loginWithGoogle} />}
        </Box> </> : <Grid lg={6} xs={12} item style={{ paddingTop: 0, padding: 30, display: 'flex', alignItems: 'center', flexDirection: 'column' }}></Grid>}
      <Grid lg={6} xs={12} item style={{ paddingTop: 0, padding: 0, display: 'flex', alignItems: 'center', flexDirection: 'column' }}>

      </Grid>
      <Grid lg={4} item style={{ paddingTop: 30, padding: window.innerWidth > 1200 ? 30: 0 }}>
        <Box sx={{ padding: 2 }} style={{ paddingBottom: 26 }}>
          <Title text={translations?.authentication} fontSize={24} uppercase={true} fontWeight='500' />
        </Box>
        <LoginForm
          {...props}
          onLogin={() => {
            initCartAndFavs(app, { ...auth, loggedIn: true }, setCart, setFavorites)
            history.push("/")
          }} />
        <Box
          className={styles.login}
          style={{
            width: '100%',
            display: 'flex',
            alignItems: window.innerWidth > 1200 ?  'left' : 'center',
            flexDirection: 'row',
            justifyContent: window.innerWidth > 1200 ?  'left' : 'center'
          }}>
          <Box sx={{ padding: 2, alignItems: 'center', cursor: 'pointer' }} onClick={() => {
            setPasswordRestoreOpen(true)
          }}>
            <Title text={translations?.forgot_password} fontSize={17} uppercase={false} fontWeight='400' />
          </Box>

        </Box>
      </Grid>
      <Grid lg={8} style={{ padding: window.innerWidth > 1200 ? 30: 0 , paddingTop: 30 }}>
        <Box sx={{ padding: 2 }}>
          <Title text={translations?.registration} fontSize={24} uppercase={true} fontWeight='500' />
        </Box>
        <RegisterForm
          {...props}
          onLogin={() => {
            history.push("/")
          }} />
      </Grid>
      {window.innerWidth > 1200 && socialLoginEnabled ? <><Box sx={{ padding: 2, paddingLeft: 6, width: '100%', marginTop: -5 }}>
        <Title text={translations?.authentication_facebook}
          style={{ padding: 0 }}
          fontSize={20} uppercase={true} fontWeight='500' />
      </Box>
        <Box style={{ padding: 0, width: '50%' , maxWidth: 345, marginLeft: 27, paddingBottom: 100 }}>
          <Box style={{ width: '100%' }}>
            {facebookLoginEnabled && <FacebookButton title={"Facebook"} onClick={loginWithFacebook} />}
          </Box>
          {googleLoginEnabled && <GoogleButton title={"Google"} onClick={loginWithGoogle} />}
        </Box> </> : ' '}
      <Grid lg={3}></Grid>
    </Grid>

    <div className={styles.login} style={{ marginTop: 20, marginLeft: 'auto', marginRight: 'auto' }}>
      <br />

    </div>
  </div>
}

const mapStateToProps = (state: any) => {
  return {
    auth: state.auth,
    app: state.app,
    translations: state.translations
  }
}

export default connect(mapStateToProps, { setSnackBar, login, setCart, setFavorites })(LoginPage)

const useStyles = makeStyles({
  container: {
    width: '100%',
    background: '#fafafa',
    textAlign: 'center',
    alignContent: 'center',
    alignItems: 'center',
    justifyContent: 'center', textflexDirection: 'column'
  },
  login: {
    flex: 1,
    display: 'flex',
    alignSelf: 'center',
    flexDirection: 'row'
  }
})
